// ---------------------------------------------------------------------------
// Project generation durability marker (G1 — 2026-07-27 design doc §2.2)
//
// The chat generation chain runs entirely in the browser: createProject
// persists a stub manifest first, and the generated blocks are persisted
// later by the requestAppGeneration MCP handler. If the browser session dies
// in between, neither persist nor cleanup runs, and the orphan stub used to
// masquerade as a normal draft (live defect: project c890b8e8 — 149-byte
// manifest, 0 studio_blocks rows).
//
// This module is the pure vocabulary for the durability contract:
//   - `settings.generation = { state:'pending', traceId, startedAt }` is
//     written when the generation flow creates the server project;
//   - the state flips to 'complete' (+ completedAt) only after the block
//     persist + readback leg succeeds;
//   - a project still 'pending' past the 10-minute threshold is an ORPHAN
//     and must be rendered distinctly (honest labeling), never as a draft.
//
// IO lives in services/project-api.ts; this module has no imports on purpose
// so every consumer (create leg, MCP handler, dashboard, ghost-job cleanup)
// shares one testable definition.
// ---------------------------------------------------------------------------

export const PROJECT_GENERATION_SETTINGS_KEY = 'generation'

/** Design doc §2.2.2 — pending + elapsed > 10 minutes ⇒ orphan surface. */
export const PROJECT_GENERATION_ORPHAN_THRESHOLD_MS = 10 * 60 * 1000

/**
 * The workflow's durable write succeeded (blocks exist, state === 'complete')
 * but the browser-side generation flow gave up BEFORE it acknowledged that
 * persistence — the execute call reported a failure, or returned a status
 * other than 'complete', and the cleanup path then chose to preserve the
 * project.
 *
 * That narrowness is exact, not approximate, and must not be widened in
 * prose: every post-acknowledgment step (`markPreview`,
 * `registerChatStartedAppInLibrary`, `persistGenerationMetadata`,
 * `navigateToGeneratedStudioProject`) runs after
 * `generatedArtifactPersistenceAcknowledged` is set, and that catch path
 * rethrows WITHOUT calling `cleanupCreatedProject` — so a failed
 * redirect/cleanup after acknowledgment is NEVER recorded here. Do not
 * describe this state as "the client-side cleanup/redirect failed".
 *
 * A user-initiated cancellation is also never recorded (see the abort
 * reason codes in start-app-generation-from-prompt.ts): the user cancelled,
 * the browser did not fail, and the preserved project is simply a normal
 * saved app.
 *
 * The generation is NOT incomplete — saying so would be false — so this
 * rides alongside `state` rather than replacing it.
 */
export interface GenerationFinalizationFailure {
  state: 'failed'
  /** ISO timestamp of the failure. */
  at: string
  /** The cleanup path's reason code, for diagnosis. */
  reasonCode: string
}

export interface ProjectGenerationMarker {
  state: 'pending' | 'complete'
  /** W0-2 D5 trace id spanning create→persist→readback, persisted so a
   * durability incident can be diagnosed server-side after the fact. */
  traceId: string
  /** ISO timestamp of the create leg. */
  startedAt: string
  /** ISO timestamp of the persist+readback success. Complete markers only. */
  completedAt?: string
  /** Set when the durable write had already succeeded but the browser-side
   * generation flow failed before acknowledging it. See
   * `GenerationFinalizationFailure` for the exact (narrow) window this
   * covers — notably NOT a post-acknowledgment redirect failure. */
  finalization?: GenerationFinalizationFailure
}

export function buildPendingGenerationMarker(
  traceId: string,
  now: Date = new Date(),
): ProjectGenerationMarker {
  return {
    state: 'pending',
    traceId,
    startedAt: now.toISOString(),
  }
}

/**
 * Flip an existing marker to complete, preserving its traceId/startedAt.
 * A pre-G1 project has no marker: record a self-standing complete marker
 * from the handler's traceId when one exists, otherwise return null —
 * there is nothing provable to record.
 */
export function completeGenerationMarker(
  existing: ProjectGenerationMarker | null,
  fallbackTraceId: string | undefined,
  now: Date = new Date(),
): ProjectGenerationMarker | null {
  const completedAt = now.toISOString()
  if (existing) {
    return {
      state: 'complete',
      traceId: existing.traceId,
      startedAt: existing.startedAt,
      completedAt,
    }
  }
  if (!fallbackTraceId) return null
  return {
    state: 'complete',
    traceId: fallbackTraceId,
    startedAt: completedAt,
    completedAt,
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/**
 * Defensive read of a `finalization` payload. A malformed shape (non-record,
 * wrong `state`, non-string/empty `at` or `reasonCode`) yields undefined —
 * the caller then omits the field entirely rather than failing the whole
 * marker read over it.
 */
function readGenerationFinalizationFailure(
  value: unknown,
): GenerationFinalizationFailure | undefined {
  const record = asRecord(value)
  if (!record) return undefined
  const { state, at, reasonCode } = record
  if (state !== 'failed') return undefined
  if (typeof at !== 'string' || !at) return undefined
  if (typeof reasonCode !== 'string' || !reasonCode) return undefined
  return { state, at, reasonCode }
}

/** Defensive read of `settings.generation`; malformed markers read as null. */
export function readProjectGenerationMarker(
  settings: unknown,
): ProjectGenerationMarker | null {
  const record = asRecord(settings)
  const marker = asRecord(record?.[PROJECT_GENERATION_SETTINGS_KEY])
  if (!marker) return null
  const { state, traceId, startedAt, completedAt, finalization } = marker
  if (state !== 'pending' && state !== 'complete') return null
  if (typeof traceId !== 'string' || !traceId) return null
  if (typeof startedAt !== 'string' || !startedAt) return null
  const failure = readGenerationFinalizationFailure(finalization)
  return {
    state,
    traceId,
    startedAt,
    ...(typeof completedAt === 'string' && completedAt ? { completedAt } : {}),
    ...(failure ? { finalization: failure } : {}),
  }
}

/** Merge the marker into settings without dropping sibling keys. */
export function withProjectGenerationMarker(
  settings: unknown,
  marker: ProjectGenerationMarker,
): Record<string, unknown> {
  return {
    ...(asRecord(settings) ?? {}),
    [PROJECT_GENERATION_SETTINGS_KEY]: marker,
  }
}

/**
 * Orphan detection (design doc §2.2.2). Only a PROVABLY stale pending
 * marker qualifies: unparseable or future startedAt cannot prove age and
 * must not smear the orphan label onto a live generation.
 */
export function isOrphanedGenerationMarker(
  marker: ProjectGenerationMarker | null | undefined,
  nowMs: number,
  thresholdMs: number = PROJECT_GENERATION_ORPHAN_THRESHOLD_MS,
): boolean {
  if (marker?.state !== 'pending') return false
  const startedAtMs = Date.parse(marker.startedAt)
  if (!Number.isFinite(startedAtMs)) return false
  const elapsed = nowMs - startedAtMs
  return elapsed > thresholdMs
}

/**
 * Record that the browser-side generation flow failed before it acknowledged
 * the server's persistence, on a project whose durable write had nonetheless
 * already succeeded. Every existing field is preserved verbatim — `state`
 * never changes, because the generation genuinely completed server-side.
 * (Narrow by construction — see `GenerationFinalizationFailure`: a failure
 * after acknowledgment, including a failed redirect, never reaches here.)
 */
export function withFailedGenerationFinalization(
  marker: ProjectGenerationMarker,
  reasonCode: string,
  now: Date = new Date(),
): ProjectGenerationMarker {
  return {
    ...marker,
    finalization: {
      state: 'failed',
      at: now.toISOString(),
      reasonCode,
    },
  }
}

/**
 * Retirement counterpart to `withFailedGenerationFinalization`: drop the
 * `finalization` notice while preserving every other field EXACTLY (`state`,
 * `traceId`, `startedAt`, `completedAt` — including its absence).
 *
 * Without this the notice would be permanent: `completeGenerationMarker` is
 * the only other writer that drops the field, and it is reachable solely
 * through a subsequent successful generation into the same project. A user
 * who does exactly what the card asks — open the app and confirm it looks
 * right — would otherwise keep seeing it on every dashboard visit forever.
 */
export function withoutGenerationFinalization(
  marker: ProjectGenerationMarker,
): ProjectGenerationMarker {
  const next: ProjectGenerationMarker = { ...marker }
  delete next.finalization
  return next
}

/** True only when the marker records a failed finalization leg. */
export function hasFailedGenerationFinalization(
  marker: ProjectGenerationMarker | null | undefined,
): boolean {
  return marker?.finalization?.state === 'failed'
}
